import { BaseViewModel } from "@iot-platform/core";
import type { ReportRouteGpsFilter } from "@report/domain/models/report-route-gps.ts";
import { ReportGpsTypeEnum } from "@report/domain/models/report-route-gps.ts";
import { ReportGetRouteGpsUseCase } from "@report/domain/usecases/report-get-route-gps.usecase.ts";
import { StationTypeEnumSchema } from "@station/data/dto/station.dto.ts";
import type { Poi, Station } from "@station/domain/models/station.model";
import { StationGetByUseCase } from "@station/domain/usecases/station-get-by.usecase.ts";
import { inject, injectable } from "inversify";
import { DateTime } from "luxon";
import { action, makeObservable, observable, runInAction } from "mobx";

const defaultToKm = 1000;

@injectable()
export class HistoricalRouteGpsFilterViewmodel extends BaseViewModel {
    @observable
    currentStation: Readonly<Station> | null = null;

    @observable
    initialLoading = true;

    @observable
    from: DateTime = DateTime.now().minus({ days: 1 });

    @observable
    to: DateTime = DateTime.now();

    @observable
    errors: { from?: string; to?: string } = {};

    @observable
    roads: string[] = [];

    @observable
    selectedRoad: string | null = null;

    @observable
    originPoiSelected: string | null = null;

    @observable
    fromKm?: number = 0;

    @observable
    toKm?: number = defaultToKm;

    @observable
    gpsReportType: ReportGpsTypeEnum = ReportGpsTypeEnum.dailyActivityGps;

    @observable
    pois: Poi[] = [];

    constructor(
        @inject(ReportGetRouteGpsUseCase)
        private readonly reportGetRouteGpsUseCase: ReportGetRouteGpsUseCase,
        @inject(StationGetByUseCase)
        private readonly stationGetByUseCase: StationGetByUseCase,
    ) {
        super();
        makeObservable(this);
    }

    override async didMount(): Promise<void> {
        await this.initViewData();
    }

    async initViewData(): Promise<void> {
        await this.fetchRouteGps();
        await this.fetchPois();

        runInAction(() => {
            this.initialLoading = false;
        });
    }

    @action
    setStation(station: Readonly<Station>): void {
        runInAction(() => {
            this.currentStation = station;
        });
    }

    @action
    async fetchPois(): Promise<void> {
        if (!this.currentStation) throw new Error("Current station is not set");
        if (this.gpsReportType !== ReportGpsTypeEnum.poisRoutes) return;

        const stationFetched = await this.stationGetByUseCase.execute(
            this.currentStation.id.valueOrThrow,
        );
        if (stationFetched?.pois) {
            runInAction(() => {
                this.pois = stationFetched.pois;
            });
        }
    }

    @action
    async fetchRouteGps(): Promise<void> {
        if (!this.currentStation) throw new Error("Current station is not set");

        if (this.currentStation.type === StationTypeEnumSchema.enum.Gps) {
            const reportRouteGpsFilter: ReportRouteGpsFilter = {
                from: this.from.toSeconds(),
                to: this.to.toSeconds(),
                stationId: Number(this.currentStation.id.value),
            };
            const routeGps =
                await this.reportGetRouteGpsUseCase.execute(
                    reportRouteGpsFilter,
                );

            this.roads = routeGps.map((route) => route.roads).flat();
        }
    }

    @action
    setSelectedRoad = (road: string | null): void => {
        if (road === null) {
            this.setFromKm(0);
            this.setToKm(defaultToKm);
        }
        runInAction(() => {
            this.selectedRoad = road;
        });
    };

    @action
    setOriginPoiSelected = (poi: string | null): void => {
        runInAction(() => {
            this.originPoiSelected = poi;
        });
    };

    @action
    setFromKm = (fromKm: number): void => {
        runInAction(() => {
            this.fromKm = fromKm;
        });
    };

    @action
    setToKm = (toKm: number): void => {
        runInAction(() => {
            this.toKm = toKm;
        });
    };

    @action
    setGpsReportType = (gpsReportType: ReportGpsTypeEnum): void => {
        runInAction(() => {
            this.gpsReportType = gpsReportType;
        });
    };
}
